<div class="modal-header">
  <h4 class="modal-title">Booking Details</h4>
  <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
    <span aria-hidden="true">&times;</span>
  </button>
</div>

<div class="modal-body">

  <div *ngIf="loading">
    <h3 class="text-muted">Loading...</h3>
  </div>

  <div class="alert alert-danger" role="alert" *ngIf="serverError">
    Something went wrong loading these details (error {{ serverError }}).
  </div>

  <ng-container *ngIf="!loading && details">

    <!-- Event info -->
    <p class="mb-3">
      <a *ngIf="getEventDetailsLink() as eventLink; else eventNameOnly"
         [routerLink]="eventLink"><b>{{ details.event_type_name }}</b></a>
      <ng-template #eventNameOnly><b>{{ details.event_type_name }}</b></ng-template>
      <ng-container *ngIf="details.event_start_datetime">
        <br>
        {{ dateStringFromISO8601(details.event_start_datetime, details.time_zone) }},
        {{ timeStringFromISO8601(details.event_start_datetime, details.time_zone) }}
      </ng-container>
      <ng-container *ngIf="details.event_num_parts > 1">
        <br>{{ details.event_num_parts }} parts
      </ng-container>
    </p>

    <dl class="row mb-0">

      <dt class="col-sm-4">Location</dt>
      <dd class="col-sm-8">{{ details.location_name }}</dd>

      <dt class="col-sm-4">Booking Status</dt>
      <dd class="col-sm-8">
        <span *ngIf="details.is_cancelled" class="text-danger"><b>Cancelled</b></span>
        <span *ngIf="!details.is_cancelled">{{ getStatusDisplay() }}</span>
      </dd>

      <dt class="col-sm-4">Booking Customer</dt>
      <dd class="col-sm-8">
        <app-nullable-customer-link [customerId]="details.booking_customer_id"
                                    [firstName]="details.booking_customer_first_name"
                                    [lastName]="details.booking_customer_last_name"
                                    section="reservations/bookings"
                                    fallbackText="Not linked"></app-nullable-customer-link>
      </dd>

      <dt class="col-sm-4">Booked At</dt>
      <dd class="col-sm-8">
        {{ dateStringFromISO8601(details.created_at, details.time_zone) }},
        {{ timeStringFromISO8601(details.created_at, details.time_zone) }}
      </dd>

      <dt class="col-sm-4">Last Updated</dt>
      <dd class="col-sm-8">
        {{ dateStringFromISO8601(details.updated_at, details.time_zone) }},
        {{ timeStringFromISO8601(details.updated_at, details.time_zone) }}
      </dd>

      <dt class="col-sm-4">Created By</dt>
      <dd class="col-sm-8">
        <div>
          <a *ngIf="getCreatedByLink() as createdByLink; else createdByPlain"
             [routerLink]="createdByLink">{{ getCreatedByText() }}</a>
          <ng-template #createdByPlain>{{ getCreatedByText() }}</ng-template>
        </div>
        <div *ngIf="details.participant_agreement_bypassed_by_name ||
                    details.participant_agreement_bypassed_by_email">
          Participant agreement bypassed by
          {{ formatNameAndEmail(details.participant_agreement_bypassed_by_name,
                                details.participant_agreement_bypassed_by_email) }}
        </div>
        <div *ngIf="details.required_waiver_bypassed_by_name ||
                    details.required_waiver_bypassed_by_email">
          Required waiver bypassed by
          {{ formatNameAndEmail(details.required_waiver_bypassed_by_name,
                                details.required_waiver_bypassed_by_email) }}
        </div>
      </dd>

      <dt class="col-sm-4">Fee</dt>
      <dd class="col-sm-8">
        <span *ngIf="details.fee_name">{{ details.fee_name }}</span>
        <span *ngIf="!details.fee_name" class="text-muted">None</span>
      </dd>

      <dt class="col-sm-4">Add-Ons</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="details.add_on_names.length > 0; else noAddOns">
          <div *ngFor="let addOnName of (details.add_on_names_display || details.add_on_names)">
            {{ addOnName }}
          </div>
        </ng-container>
        <ng-template #noAddOns><span class="text-muted">None</span></ng-template>
      </dd>

      <dt class="col-sm-4">Per-Booking Fee</dt>
      <dd class="col-sm-8">{{ formatMoney(details.per_booking_base_fee) }}</dd>

      <dt class="col-sm-4">Add-On Fees</dt>
      <dd class="col-sm-8">{{ formatMoney(details.per_booking_add_on_fee) }}</dd>

      <ng-container *ngIf="details.per_booking_deposit !== null">
        <dt class="col-sm-4">Deposit</dt>
        <dd class="col-sm-8">{{ formatMoney(details.per_booking_deposit) }}</dd>
      </ng-container>

      <dt class="col-sm-4">Purchase Price</dt>
      <dd class="col-sm-8">{{ formatMoney(details.invoice_amount) }}</dd>

      <dt class="col-sm-4">Tax</dt>
      <dd class="col-sm-8">{{ formatMoney(details.invoice_tax_amount) }}</dd>

      <dt class="col-sm-4">Paid</dt>
      <dd class="col-sm-8">{{ formatMoney(details.invoice_amount_paid) }}</dd>

      <dt class="col-sm-4">Due</dt>
      <dd class="col-sm-8">
        <span [class.text-danger]="amountIsPositive(details.invoice_amount_due)">
          {{ formatMoney(details.invoice_amount_due) }}
        </span>
      </dd>

      <ng-container *ngIf="amountIsPositive(details.invoice_refunded_amount)">
        <dt class="col-sm-4">Refunded</dt>
        <dd class="col-sm-8">{{ formatMoney(details.invoice_refunded_amount) }}</dd>
      </ng-container>

      <ng-container *ngIf="details.num_vouchers_used > 0">
        <dt class="col-sm-4">Vouchers Used</dt>
        <dd class="col-sm-8">
          {{ details.num_vouchers_used }} ({{ formatMoney(details.vouchers_amount_paid) }})
        </dd>
      </ng-container>

      <dt class="col-sm-4">Payment Method</dt>
      <dd class="col-sm-8">
        <!-- The helper's "Unknown" would be misleading on a booking that was
             never paid for (a roster or comped booking). -->
        <ng-container *ngIf="details.completed_payments.length > 0; else noPaymentMethod">
          {{ getPaymentMethodTypeDisplay(details.invoice_payment_type,
                                         details.invoice_payment_method_type) }}
        </ng-container>
        <ng-template #noPaymentMethod><span class="text-muted">None</span></ng-template>
      </dd>

      <dt class="col-sm-4">Payments</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="details.completed_payments.length > 0; else noPayments">
          <div *ngFor="let payment of details.completed_payments">
            <a [routerLink]="'/payments'"
               [queryParams]="getPaymentQueryParams(payment)"
               target="_blank">
              Payment #{{ payment.id }}
            </a>
            &mdash; {{ formatMoney(payment.amount) }},
            {{ dateStringFromISO8601(payment.created_at, details.time_zone) }}
            <span class="text-muted" *ngIf="amountIsPositive(payment.amount_refunded)">
              (refunded {{ formatMoney(payment.amount_refunded) }})
            </span>
          </div>
        </ng-container>
        <ng-template #noPayments><span class="text-muted">None</span></ng-template>
      </dd>

      <dt class="col-sm-4">Booking Questions</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="getQuestionNames(details.question_responses).length > 0; else noBookingQuestions">
          <div *ngFor="let questionName of getQuestionNames(details.question_responses)" class="mb-2">
            {{ questionName }}:
            <div *ngIf="propExistsAndIsNonEmpty(details.question_responses[questionName])"
                 class="question-answer">{{ formatQuestionAnswer(details.question_responses[questionName]) }}</div>
            <div class="text-muted" *ngIf="!propExistsAndIsNonEmpty(details.question_responses[questionName])">
              No response
            </div>
          </div>
        </ng-container>
        <ng-template #noBookingQuestions>
          <span class="text-muted">None</span>
        </ng-template>
      </dd>

      <dt class="col-sm-4">Custom Customer Fields</dt>
      <dd class="col-sm-8">
        <ng-container *ngIf="details.custom_customer_field_values.length > 0; else noCustomFields">
          <div *ngFor="let fieldValue of details.custom_customer_field_values" class="mb-2">
            <span *ngIf="fieldValue.field_name">{{ fieldValue.field_name }}</span>
            <span *ngIf="!fieldValue.field_name" class="text-muted">Removed field</span>:
            <div class="question-answer">{{ fieldValue.values.join(', ') }}</div>
          </div>
        </ng-container>
        <ng-template #noCustomFields>
          <span class="text-muted">None</span>
        </ng-template>
      </dd>

    </dl>

    <h6 class="mt-3">Participants</h6>
    <div class="rounded-table-wrapper rounded-table-tight table-responsive">
      <table class="table table-12px mb-0">
        <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Age</th>
          <th scope="col">Cancelled</th>
          <th scope="col">Check-Ins</th>
          <th scope="col"></th>
        </tr>
        </thead>
        <tbody>
        <tr *ngFor="let participant of details.participants">
          <td>
            <app-customer-link *ngIf="participant.customer_id"
                               [customerId]="participant.customer_id"
                               [firstName]="participant.first_name"
                               [lastName]="participant.last_name"
                               section="reservations/upcoming"></app-customer-link>
            <span *ngIf="!participant.customer_id && getParticipantName(participant)">
              {{ getParticipantName(participant) }}
            </span>
            <span *ngIf="!participant.customer_id && !getParticipantName(participant)"
                  class="text-muted">
              Details not entered
            </span>
          </td>
          <td>
            <span *ngIf="participant.birthday">{{ getAge(participant.birthday) }}</span>
            <span *ngIf="!participant.birthday" class="text-muted">Unknown</span>
          </td>
          <td>
            <span *ngIf="participant.is_cancelled" class="text-danger">Cancelled</span>
            <span *ngIf="!participant.is_cancelled" class="text-muted">&mdash;</span>
          </td>
          <td class="text-nowrap">{{ getCheckInsDisplay(participant) }}</td>
          <td class="table-button-cell">
            <a [routerLink]="'/reporting/reservations'"
               [queryParams]="getParticipantQueryParams(participant)"
               target="_blank"
               class="btn btn-sm btn-secondary table-12px-button float-right">
              View
            </a>
          </td>
        </tr>
        <tr *ngIf="details.participants.length === 0">
          <td colspan="99" class="text-muted">No participants</td>
        </tr>
        </tbody>
      </table>
    </div>

    <dl class="row mb-0 mt-3">
      <dt class="col-sm-4">Booking ID</dt>
      <dd class="col-sm-8">{{ details.id }}</dd>

      <dt class="col-sm-4">Invoice ID</dt>
      <dd class="col-sm-8">{{ details.invoice_id }}</dd>
    </dl>

  </ng-container>

</div>
